import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { TOAST } from '../../utils/toastMessages';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useAuth, API } from '../../App';

const AdminProfileTab = () => {
  const { user } = useAuth();
  
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [updatingPassword, setUpdatingPassword] = useState(false);
  
  const [securityQuestions, setSecurityQuestions] = useState([]);
  const [securityAnswers, setSecurityAnswers] = useState([]);
  const [currentPasswordForSecurity, setCurrentPasswordForSecurity] = useState('');
  const [updatingSecurityQuestions, setUpdatingSecurityQuestions] = useState(false);

  useEffect(() => {
    fetchSecurityQuestions();
  }, []);

  const fetchSecurityQuestions = async () => {
    try {
      const response = await axios.get(`${API}/auth/security-questions`);
      setSecurityQuestions(response.data.questions);
      setSecurityAnswers(response.data.questions.map(q => ({ question: q, answer: '' })));
    } catch (error) {
      console.error('Failed to load security questions:', error);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      toast.error('Please fill all password fields');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 10) {
      toast.error('New password must be at least 10 characters');
      return;
    }

    setUpdatingPassword(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/auth/change-password`,
        {
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      toast.success(TOAST.PASSWORD_CHANGED);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setUpdatingPassword(false);
    }
  };

  const handleSecurityQuestionsUpdate = async (e) => {
    e.preventDefault();

    if (!currentPasswordForSecurity) {
      toast.error('Please enter your current password');
      return;
    }

    const filledAnswers = securityAnswers.filter(a => a.answer.trim() !== '');
    if (filledAnswers.length !== 3) {
      toast.error('Please answer all 3 security questions');
      return;
    }

    setUpdatingSecurityQuestions(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/auth/update-security-questions`,
        {
          current_password: currentPasswordForSecurity,
          security_answers: securityAnswers
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      toast.success(TOAST.SECURITY_QUESTIONS_UPDATED);
      setCurrentPasswordForSecurity('');
      setSecurityAnswers(securityQuestions.map(q => ({ question: q, answer: '' })));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update security questions');
    } finally {
      setUpdatingSecurityQuestions(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Admin Profile</CardTitle>
        <CardDescription>Manage your admin account settings</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Admin Info */}
        <div className="border-b pb-4">
          <h3 className="text-lg font-semibold mb-2">Account Information</h3>
          <p className="text-sm text-gray-600">Name: {user?.name}</p>
          <p className="text-sm text-gray-600">Email: {user?.email}</p>
          <p className="text-sm text-gray-600">Role: Administrator</p>
        </div>

        {/* Password Change Section */}
        <div className="border-t pt-4">
          <Label className="text-base font-semibold">Change Password</Label>
          <p className="text-sm text-gray-500 mb-3">Update your admin password</p>
          <form onSubmit={handlePasswordChange} className="space-y-3">
            <div>
              <Label htmlFor="currentPassword">Current Password *</Label>
              <Input
                id="currentPassword"
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                placeholder="Enter current password"
              />
            </div>
            <div>
              <Label htmlFor="newPassword">New Password *</Label>
              <Input
                id="newPassword"
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                placeholder="Enter new password"
              />
              <p className="text-xs text-gray-500 mt-1">Minimum 10 characters, 3+ character types</p>
            </div>
            <div>
              <Label htmlFor="confirmPassword">Confirm New Password *</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                placeholder="Confirm new password"
              />
            </div>
            <Button
              type="submit"
              disabled={updatingPassword}
              variant="outline"
              fullWidth="mobile"
            >
              {updatingPassword ? 'Changing...' : 'Change Password'}
            </Button>
          </form>
        </div>

        {/* Security Questions Section */}
        <div className="border-t pt-4">
          <Label className="text-base font-semibold">Update Security Questions</Label>
          <p className="text-sm text-gray-500 mb-3">Used for password recovery</p>
          <form onSubmit={handleSecurityQuestionsUpdate} className="space-y-3">
            {securityQuestions.map((question, index) => (
              <div key={index}>
                <Label>{question} *</Label>
                <Input
                  type="text"
                  value={securityAnswers[index]?.answer || ''}
                  onChange={(e) => {
                    const newAnswers = [...securityAnswers];
                    newAnswers[index] = { question, answer: e.target.value };
                    setSecurityAnswers(newAnswers);
                  }}
                  placeholder="Your answer"
                />
              </div>
            ))}
            <div>
              <Label htmlFor="currentPasswordSecurity">Current Password (to confirm) *</Label>
              <Input
                id="currentPasswordSecurity"
                type="password"
                value={currentPasswordForSecurity}
                onChange={(e) => setCurrentPasswordForSecurity(e.target.value)}
                placeholder="Enter current password"
              />
            </div>
            <Button
              type="submit"
              disabled={updatingSecurityQuestions}
              variant="outline"
              fullWidth="mobile"
            >
              {updatingSecurityQuestions ? 'Updating...' : 'Update Questions'}
            </Button>
          </form>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminProfileTab;
